import React, { useEffect } from "react";
import { Modal } from "@mui/material";
import PropTypes from "prop-types";
import { GRADIENTS } from "utils/designTokens";

// PorterIQ Components
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import MarkdownRenderer from "components/MarkdownRenderer";
import ThinkingAnimation from "components/ThinkingAnimation";
import StandardMessageDisplay from "components/StandardMessageDisplay";

// Data Hooks
import useQuickCatchupData from "pages/competitor-reports/data/useQuickCatchupData";

const modalShell = {
  width: '63%',
  maxWidth: '700px',
  outline: 'none',
};

const QuickCatchupModal = ({ isOpen, onClose, reportType, entityId, entityName }) => {

  const { briefing, loading, error, fetchData } = useQuickCatchupData(
    reportType,
    {
      competitorId: reportType !== 'news' ? entityId : null,
      pressId: reportType === 'news' ? entityId : null,
    }
  );

  // This useEffect hook controls the data fetching
  // It runs when the modal's open state (`isOpen`) changes.
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);


  const getTitle = () => {
    if (reportType === 'news' && !entityId) {
      return `Recap of the latest news from All Press Sources`;
    }
    if (reportType === 'news' && entityId) {
      return `Recap of the latest news from ${entityName}`;
    }
    return `Recap of ${entityName}'s Latest Activities`;
  };

  const isPlaceholder = loading || error || !briefing;

  const getBody = () => {
    if (loading) {
      return <ThinkingAnimation message="Thinking... Hold on a moment please." />;
    }
    if (error) {
      return <StandardMessageDisplay type="error" />;
    }
    if (!briefing) {
      return <StandardMessageDisplay type="info" title="No Data" message="No summary is available for the selected period." />;
    }
    return <MarkdownRenderer markdownText={briefing} />;
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
      }}
    >
      <VuiBox sx={modalShell}>
        <GlassCard sx={{ minHeight: '315px', background: "rgba(18, 22, 45, 0.78)", backdropFilter: "blur(20px)", "& > .MuiBox-root": { padding: "24px" }, "&:hover": { transform: "none", boxShadow: "none" } }}>
          <VuiBox sx={{ display: 'flex', flexDirection: 'column', minHeight: '270px' }}>
            <VuiTypography variant="h5" color="white" fontWeight="bold" mb={2} sx={{
              background: GRADIENTS.text,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}>
              {getTitle()}
            </VuiTypography>
            <VuiBox sx={{
              flex: 1,
              display: 'flex',
              alignItems: isPlaceholder ? 'center' : 'flex-start',
              justifyContent: isPlaceholder ? 'center' : 'flex-start',
              overflowY: 'auto',
            }}>
              {getBody()}
            </VuiBox>
          </VuiBox>
        </GlassCard>
      </VuiBox>
    </Modal>
  );
};

QuickCatchupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportType: PropTypes.oneOf(['news', 'products', 'digital-presence']).isRequired,
  entityId: PropTypes.string, // The ID of the competitor or press source
  entityName: PropTypes.string, // The display name
};

QuickCatchupModal.defaultProps = {
  entityId: null,
  entityName: "the selected source",
};

export default QuickCatchupModal; 